import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon as CompassIcon } from "@circleco/compass/components/Icon";
import { Typography } from "@circleco/compass/components/Typography";
// Dock nav links and sibling dock components (community header, build menu,
// recent chats) all navigate via TanStack Router.
import { Link, useRouterState } from "@tanstack/react-router";

import { DOCK_NAV_ACTIVE_CLASSES, DOCK_NAV_ROW_CLASSES } from "./constants";
import { DockNavBadgeDot } from "./DockNavBadgeDot";
import { isPathActive } from "./isPathActive";
import type { DockNavItem } from "./types";

interface CircleAIDockNavLinkProps {
  item: DockNavItem;
}

export const CircleAIDockNavLink = ({ item }: CircleAIDockNavLinkProps) => {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const isActive = item.to
    ? isPathActive({ exact: item.exact, pathname, to: item.to })
    : Boolean(item.isActive);
  const iconName = isActive && item.activeIcon ? item.activeIcon : item.icon;

  const content = (
    <>
      <div className="relative">
        <CompassIcon
          name={iconName}
          size="md"
          className="shrink-0"
          color="primary"
        />
        {item.badge && <DockNavBadgeDot />}
      </div>
      <Typography variant="body-sm" color="primary" truncate>
        {item.label}
      </Typography>
    </>
  );

  if (item.to) {
    return (
      <Link
        to={item.to}
        data-testid={item["data-testid"]}
        data-circle-ai-toggle={item.id === "circle-ai" ? "dock" : undefined}
        data-dock-active={isActive || undefined}
        activeOptions={{ exact: item.exact ?? false, includeSearch: false }}
        className={classNames(
          DOCK_NAV_ROW_CLASSES,
          isActive && DOCK_NAV_ACTIVE_CLASSES,
        )}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={item.onClick}
      disabled={item.disabled}
      data-testid={item["data-testid"]}
      data-circle-ai-toggle={item.id === "circle-ai" ? "dock" : undefined}
      data-dock-active={isActive || undefined}
      className={classNames(
        DOCK_NAV_ROW_CLASSES,
        item.disabled && "pointer-events-none opacity-50",
        isActive && DOCK_NAV_ACTIVE_CLASSES,
      )}
      {...(item.isActive !== undefined && { "aria-pressed": item.isActive })}
    >
      {content}
    </button>
  );
};
